import { isEqual, uniq } from 'lodash'
import { asNumber, compactSymbolObject, hasIntersection, pickSymbols, valuesOfSymbolObject } from '@ota-frontend/core/utils'
import Areas from './area'
import DeparturePlaces from './departurePlace'
import Facets from './facet'

export const _BASIC_PARAMS = Object.freeze(Object.fromEntries([
  'SEARCH_TYPE',  // _SEARCH_TYPES の value 参照
  'DOMESTIC',  // '0' or '1'  boolean の方が普通かもしれない
  'DEPARTURE_PLACE',  // int
  'REGION_OF_DESTINATION1', 'REGION_OF_DESTINATION2', 'REGION_OF_DESTINATION3', 'REGION_OF_DESTINATION4',  // int
  'COUNTRY_OF_DESTINATION1', 'COUNTRY_OF_DESTINATION2', 'COUNTRY_OF_DESTINATION3', 'COUNTRY_OF_DESTINATION4',  // int
  'CITY_OF_DESTINATION1', 'CITY_OF_DESTINATION2', 'CITY_OF_DESTINATION3', 'CITY_OF_DESTINATION4',  // int
  'DISTRICT_OF_DESTINATION1', 'DISTRICT_OF_DESTINATION2', 'DISTRICT_OF_DESTINATION3', 'DISTRICT_OF_DESTINATION4',  // int
  'DEPARTURE_MONTH',  // string, e.g. 2020-01
  'DEPARTURE_DAY',  // string, e.g. 07
  'DURATION_FROM',  // int
  'DURATION_TO',  // int
  'NUMBER_OF_PASSENGERS',  // int
].map(name => [name, Symbol(name)])))

// 絞り込み検索条件
export const _FILTER_PARAMS = Object.freeze(Object.fromEntries([
  'CAMPAIGNS',  // array of string
  'KEYWORD',  // string
  'PRICE_FROM',  // int
  'PRICE_TO',  // int
  'INTENTIONS',  // array of string, e.g. 11,22-33,44
  'MIN_HOTEL_RANK',  // int
  'HOTELS_OF_DESTINATION1', 'HOTELS_OF_DESTINATION2', 'HOTELS_OF_DESTINATION3', 'HOTELS_OF_DESTINATION4',  // array of string
  'STOPOVER',  // '0' => 直行便
  'TRAIN',  // string
  'AIRLINES',  // array of string
  'SEAT_CODE',  // string
  'DEPARTURE_PLACE_TIME_CODE',  // string
  'DESTINATION_ARRIVAL_TIME_CODE',  // string
  'DESTINATION_TIME_CODE',  // string
  'DEPARTURE_PLACE_ARRIVAL_TIME_CODE',  // string
].map(name => [name, Symbol(name)])))

export const _META_PARAMS = Object.freeze(Object.fromEntries([
  'ORDER',  // string
  'CODE',  // string
].map(name => [name, Symbol(name)])))

export const _PARAMS = {
  ..._BASIC_PARAMS,
  ..._FILTER_PARAMS,
  ..._META_PARAMS,
}

export const _SEARCH_TYPES = {
  EITHER: '0',  // 指定都市のうち、いずれかを含む
  CONTAIN: '1',  // 指定都市をすべて含む
  ONLY: '2',  // 指定都市のみ
}

const defaultAdditionals = {
  areas: new Areas(),
  departurePlaces: new DeparturePlaces(),
  facets: new Facets(),
}

// 検索条件を表すモデル
export default class SearchCondition {
  constructor(params = {}, additionals = {}) {
    this._raw = Object.freeze(compactSymbolObject(params))
    this._additionals = {
      ...defaultAdditionals,
      ...additionals,
    }
    this._additionals.facets = this.isDomestic
      ? this._additionals.facets.asDomestic
      : this._additionals.facets.asAbroad
  }

  static get PARAMS() {
    return _PARAMS
  }

  static get SEARCH_TYPES() {
    return _SEARCH_TYPES
  }

  static get ORDERS() {
    return [
      { name: 'おすすめ順', code: 'price_fav' },
      { name: '安い順', code: 'price_asc' },
      { name: '高い順', code: 'price_desc' },
      { name: '新着順', code: 'update' },
      { name: '人気順', code: 'pageview' },
    ]
  }

  get raw() {
    return this._raw
  }

  // --------------------------
  // クラス全体に関する知識・操作

  get empty() {
    return new SearchCondition({}, this._additionals)
  }

  get isEmpty() {
    return Object.getOwnPropertySymbols(this.raw).length === 0
  }

  isEqualTo(another) {
    return isEqual(this.raw, another.raw)
  }

  withDefaults(defaults = {}) {
    return new SearchCondition({
      ...defaults,
      ...this.raw,
    }, this._additionals)
  }

  update(updates = {}) {
    return new SearchCondition({
      ...this.raw,
      ...updates,
    }, this._additionals)
  }

  remove(name) {
    return this.removeAll([name])
  }

  removeAll(names) {
    const removals = Object.fromEntries(names.map(name => [name, undefined]))
    return this.update(removals)
  }

  clone() {
    return this.update()
  }

  basicConditionIsDifferentFrom(another) {
    return Object.values(_BASIC_PARAMS).some(c => this.raw[c] !== another.raw[c])
  }

  get filters() {
    return new SearchCondition(filtersOf(this.raw), this._additionals)
  }

  get withoutFilters() {
    return this.removeAll(Object.values(_FILTER_PARAMS))
  }

  get withoutMeta() {
    return this.removeAll(Object.values(_META_PARAMS))
  }

  get hasFilters() {
    return valuesOfSymbolObject(filtersOf(this.raw)).length > 0
  }

  get withoutUnselectables() {
    return this
      .update({
        [_PARAMS.AIRLINES]: this.airlineIds.filter(id => this.facets.airlines.some(a => a.code === id)),
        [_PARAMS.TRAIN]: this.facets.trains.some(t => t.code === this.trainIds[0]) ? this.trainIds[0] : undefined,
        [_PARAMS.INTENTIONS]: this.groupedIntentionIds
          .filter(ids => hasIntersection(ids, this.facets.intentionIds))
          .map(ids => this.facets.intentions.find(i => hasIntersection(i.codes, ids)))
          .filter(Boolean)  // 不要だと思われるが
          .map(i => i.codes.join('-')),
      })
      .selectHotels(
        this.destinations[0].hotelIds.filter(id => this.facets.hotels.some(h => h.code === id)),
        this.destinations[1].hotelIds.filter(id => this.facets.hotels.some(h => h.code === id)),
        this.destinations[2].hotelIds.filter(id => this.facets.hotels.some(h => h.code === id)),
        this.destinations[3].hotelIds.filter(id => this.facets.hotels.some(h => h.code === id)),
      )
  }

  // --------
  // 追加情報

  get areas() {
    return this._additionals.areas
  }

  get departurePlaces() {
    return this._additionals.departurePlaces
  }

  get facets() {
    return this._additionals.facets
  }

  _withAdditionals(additionals) {
    return new SearchCondition(this.raw, additionals)
  }

  withAreas(areas) {
    return this._withAdditionals({ ...this._additionals, areas })
  }

  withDeparturePlaces(departurePlaces) {
    return this._withAdditionals({ ...this._additionals, departurePlaces })
  }

  withFacets(facets) {
    return this._withAdditionals({ ...this._additionals, facets })
  }

  // -------------------------
  // 検索条件に関する知識・操作

  get searchType() {
    return this.raw[_PARAMS.SEARCH_TYPE]
  }

  get rawDomestic() {
    return this.raw[_PARAMS.DOMESTIC]
  }

  get domesticFlag() {
    return this.rawDomestic ||
      (
        this.raw[_PARAMS.REGION_OF_DESTINATION1]
          ? this.raw[_PARAMS.REGION_OF_DESTINATION1] === 13 ? '1' : '0'  // 13 は国内
          : undefined
      )
  }

  get domesticFlagIsSpecified() {
    return !!this.domesticFlag
  }

  get isDomestic() {
    return this.domesticFlag === '1'
  }

  get departurePlaceId() {
    return this.raw[_PARAMS.DEPARTURE_PLACE]
  }

  get departurePlaceName() {
    return this.departurePlaceId
      ? this.departurePlaces.resolveName(this.departurePlaceId)
      : ''
  }

  get destinations() {
    return [
      new Destination(
        this.raw[_PARAMS.REGION_OF_DESTINATION1],
        this.raw[_PARAMS.COUNTRY_OF_DESTINATION1],
        this.raw[_PARAMS.CITY_OF_DESTINATION1],
        this.raw[_PARAMS.DISTRICT_OF_DESTINATION1],
        this.raw[_PARAMS.HOTELS_OF_DESTINATION1],
        { areas: this.areas },
      ),
      new Destination(
        this.raw[_PARAMS.REGION_OF_DESTINATION2],
        this.raw[_PARAMS.COUNTRY_OF_DESTINATION2],
        this.raw[_PARAMS.CITY_OF_DESTINATION2],
        this.raw[_PARAMS.DISTRICT_OF_DESTINATION2],
        this.raw[_PARAMS.HOTELS_OF_DESTINATION2],
        { areas: this.areas },
      ),
      new Destination(
        this.raw[_PARAMS.REGION_OF_DESTINATION3],
        this.raw[_PARAMS.COUNTRY_OF_DESTINATION3],
        this.raw[_PARAMS.CITY_OF_DESTINATION3],
        this.raw[_PARAMS.DISTRICT_OF_DESTINATION3],
        this.raw[_PARAMS.HOTELS_OF_DESTINATION3],
        { areas: this.areas },
      ),
      new Destination(
        this.raw[_PARAMS.REGION_OF_DESTINATION4],
        this.raw[_PARAMS.COUNTRY_OF_DESTINATION4],
        this.raw[_PARAMS.CITY_OF_DESTINATION4],
        this.raw[_PARAMS.DISTRICT_OF_DESTINATION4],
        this.raw[_PARAMS.HOTELS_OF_DESTINATION4],
        { areas: this.areas },
      ),
    ]
  }

  get destinationCountryIds() {
    return this.destinations.map(d => d.countryId).filter(Boolean)
  }

  get destinationCityIds() {
    return this.destinations.map(d => d.cityId).filter(Boolean)
  }

  get isMultiCountry() {
    return uniq(this.destinationCountryIds).length > 1
  }

  get hasMultipleDestinations() {
    return this.destinations.filter(d => d.isSpecified).length > 1
  }

  // 指定された順番以降の目的地を削除する (1～4 を指定可能)
  omitDestinationUpToNth(index) {
    const removals = [
      [
        _PARAMS.REGION_OF_DESTINATION2,
        _PARAMS.COUNTRY_OF_DESTINATION2,
        _PARAMS.CITY_OF_DESTINATION2,
        _PARAMS.DISTRICT_OF_DESTINATION2,
        _PARAMS.HOTELS_OF_DESTINATION2,
      ],
      [
        _PARAMS.REGION_OF_DESTINATION3,
        _PARAMS.COUNTRY_OF_DESTINATION3,
        _PARAMS.CITY_OF_DESTINATION3,
        _PARAMS.DISTRICT_OF_DESTINATION3,
        _PARAMS.HOTELS_OF_DESTINATION3,
      ],
      [
        _PARAMS.REGION_OF_DESTINATION4,
        _PARAMS.COUNTRY_OF_DESTINATION4,
        _PARAMS.CITY_OF_DESTINATION4,
        _PARAMS.DISTRICT_OF_DESTINATION4,
        _PARAMS.HOTELS_OF_DESTINATION4,
      ],
    ]
      .slice(index - 1)
      .flatMap(x => x)
    return this.removeAll(removals)
  }

  // 全目的地の area (country) を配列として返す (areas が必要)
  get destinationCountries() {
    return this.areas.findCountriesByIds(this.destinationCountryIds)
  }

  // 全目的地の area (city) を配列として返す (areas が必要)
  get destinationCities() {
    return this.areas.findCitiesByIds(this.destinationCityIds)
  }

  // 最初の目的地（通常第１目的地）の area (country) を返す (areas が必要)
  get primaryDestinationCountry() {
    // 何番目かの目的地に都市 (city_id) が指定されていれば都市を優先
    const areas = this.destinationCityIds.length > 0
      ? this.areas.findCitiesByIds(this.destinationCityIds)
      : this.areas.findCountriesByIds(this.destinationCountryIds)
    return areas[0]
  }

  // 最初の目的地（通常第１目的地）の area (city) を返す (areas が必要)
  get primaryDestinationCity() {
    return this.destinationCities[0]
  }

  // 日付の 'd' は、https://www.tabikobo.com/area/hawaii/ のような一部 LP から遷移してくる際に 01 ではなく 1 が設定される
  // 本来は呼び元を修正するのが上策だが、仕方ないのでここで補正する
  // URLパラメータ見直しのタイミングで必ず LP 側の問題を解決するべき
  // 'y-m'がない場合、'd' は未設定として扱う
  get _d() {
    return this.raw[_PARAMS.DEPARTURE_MONTH] && this.raw[_PARAMS.DEPARTURE_DAY]
      ? this.raw[_PARAMS.DEPARTURE_DAY].padStart(2, '0')
      : ''
  }

  get departureMonth() {  // 1 ～ 12
    if (!this.raw[_PARAMS.DEPARTURE_MONTH]) return undefined

    const month = this.raw[_PARAMS.DEPARTURE_MONTH].match(/^[0-9]{4}-(0[1-9]|1[012])$/)
    return month && month[1] ? Number(month[1]) : undefined
  }

  get departureDay() {
    if (!this.raw[_PARAMS.DEPARTURE_MONTH]) return undefined
    return asNumber(this._d)
  }

  get departureDateIsFullySpecified() {
    return !!this.departureMonth && !!this.departureDay
  }

  get departureDateAsIsoFormat() {
    if (this.departureDateIsFullySpecified) return `${this.raw[_PARAMS.DEPARTURE_MONTH]}-${this._d}`
    if (this.departureMonth) return this.raw[_PARAMS.DEPARTURE_MONTH]
    return undefined
  }

  get departureDateObject() {
    return this.departureDateIsFullySpecified ? new Date(this.departureDateAsIsoFormat) : undefined
  }

  updateDepartureMonth(isoFormat) {  // yyyy-mm
    return this.update({
      [_PARAMS.DEPARTURE_MONTH]: isoFormat,
      [_PARAMS.DEPARTURE_DAY]: null,
    })
  }

  updateDepartureDate(isoFormat) {  // yyyy-mm-dd
    return this.update({
      [_PARAMS.DEPARTURE_MONTH]: isoFormat.slice(0, 7),
      [_PARAMS.DEPARTURE_DAY]: isoFormat.slice(8),
    })
  }

  unsetDepartureDate() {
    return this.removeAll([_PARAMS.DEPARTURE_MONTH, _PARAMS.DEPARTURE_DAY])
  }

  get durationFrom() {
    return this.raw[_PARAMS.DURATION_FROM]
  }

  get durationTo() {
    return this.raw[_PARAMS.DURATION_TO]
  }

  get numberOfPassengers() {
    return this.raw[_PARAMS.NUMBER_OF_PASSENGERS]
  }

  get campaignIds() {
    return this.raw[_PARAMS.CAMPAIGNS] || []
  }

  get keyword() {
    return this.raw[_PARAMS.KEYWORD]
  }

  get priceFrom() {
    return this.raw[_PARAMS.PRICE_FROM]
  }

  get priceTo() {
    return this.raw[_PARAMS.PRICE_TO]
  }

  // name 毎に ID をグループ化したもの, e.g. [['11'], ['22', '33'], ['44']]
  // URL パラメータとしては intention_ids=11,22-33,44
  get groupedIntentionIds() {
    return (this.raw[_PARAMS.INTENTIONS] || []).map(chunk => chunk.split('-'))
  }

  // groupedIntentionIds のグループ化を解除して平坦化したもの
  // ['11', '22', '33', '44']
  get intentionIds() {
    return this.groupedIntentionIds.flatMap(x => x)
  }

  get intentions() {
    const ids = this.intentionIds
    return this.facets.intentions
      .filter(group => hasIntersection(group.codes, ids))
  }

  hasIntention(ids) {
    return hasIntersection(ids, this.intentionIds)
  }

  // intention は同一名のグループ単位で選択・選択解除する
  // 同一名称のID ['11', '22', '33'] が選択されているときに ['22', '33', '44'] を指定された場合は ['22', '33', '44'] に置き換える
  selectIntention(idsOfName) {
    const withoutSpecifiedOne = this.unselectIntention(idsOfName)
    return withoutSpecifiedOne
      .update({
        [_PARAMS.INTENTIONS]: uniq([
          ...withoutSpecifiedOne.groupedIntentionIds,
          idsOfName,
        ].map(ids => ids.join('-'))),
      })
  }

  unselectIntention(idsOfName) {
    return this.update({
      [_PARAMS.INTENTIONS]: this.groupedIntentionIds
        .filter(ids => !hasIntersection(ids, idsOfName))
        .map(ids => ids.join('-')),
    })
  }

  filterIntentionsByFacets(intentions) {
    const shouldKeep = ids => intentions.some(p => hasIntersection(p.codes, ids))
    return this.update({
      [_PARAMS.INTENTIONS]: this.groupedIntentionIds
        .filter(shouldKeep)
        .map(ids => ids.join('-')),
    })
  }

  get minHotelRank() {
    return this.raw[_PARAMS.MIN_HOTEL_RANK]
  }

  // 2021/11 以前の旧 SP版 でのみ設定されていたパラメータで、1234 を結合しただけのもの
  get hotelIds() {
    return uniq(this.destinations.flatMap(d => d.hotelIds))
  }

  get hotels() {
    return this.hotelIds
      .map(id => this.facets.findHotelByCode(id))
      .filter(Boolean)
  }

  selectHotels(
    hotelsOfDestination1,
    hotelsOfDestination2,
    hotelsOfDestination3,
    hotelsOfDestination4,
  ) {
    return this.update({
      [_PARAMS.HOTELS_OF_DESTINATION1]: hotelsOfDestination1,
      [_PARAMS.HOTELS_OF_DESTINATION2]: hotelsOfDestination2,
      [_PARAMS.HOTELS_OF_DESTINATION3]: hotelsOfDestination3,
      [_PARAMS.HOTELS_OF_DESTINATION4]: hotelsOfDestination4,
    })
  }

  unselectHotel(id) {
    const hotelId1 = this.destinations[0].hotelIds.filter(id_ => id_ !== id)
    const hotelId2 = this.destinations[1].hotelIds.filter(id_ => id_ !== id)
    const hotelId3 = this.destinations[2].hotelIds.filter(id_ => id_ !== id)
    const hotelId4 = this.destinations[3].hotelIds.filter(id_ => id_ !== id)
    return this.selectHotels(hotelId1, hotelId2, hotelId3, hotelId4)
  }

  get preferDirectFlight() {
    return this.raw[_PARAMS.STOPOVER] === '0'
  }

  get transportations() {
    return [...this.trainIds, ...this.airlineIds]
  }

  get trainIds() {
    return this.raw[_PARAMS.TRAIN] ? [this.raw[_PARAMS.TRAIN]] : []
  }

  get trains() {
    return this.trainIds
      .map(id => this.facets.trains.find(t => t.code === id))
      .filter(Boolean)
  }

  get airlineIds() {
    return this.raw[_PARAMS.AIRLINES] || []
  }

  unselectAirline(id) {
    return this.update({
      [_PARAMS.AIRLINES]: this.airlineIds.filter(a => a !== id),
    })
  }

  get airlines() {
    return this.airlineIds
      .map(id => this.facets.airlines.find(a => a.code === id))
      .filter(Boolean)
  }

  hasTransportation(id) {
    return this.transportations.includes(id)
  }

  get seatCode() {
    return this.raw[_PARAMS.SEAT_CODE]
  }

  get seat() {
    return this.seatCode ? this.facets.seats.find(s => s.code === this.seatCode) : undefined
  }

  get departurePlaceTimeCode() {
    return this.raw[_PARAMS.DEPARTURE_PLACE_TIME_CODE]
  }

  get departurePlaceTime() {
    return this.departurePlaceTimeCode ? this.facets.outwards.find(o => o.code === this.departurePlaceTimeCode) : undefined
  }

  get destinationArrivalTimeCode() {
    return this.raw[_PARAMS.DESTINATION_ARRIVAL_TIME_CODE]
  }

  get destinationArrivalTime() {
    return this.destinationArrivalTimeCode ? this.facets.arrivals.find(a => a.code === this.destinationArrivalTimeCode) : undefined
  }

  get destinationTimeCode() {
    return this.raw[_PARAMS.DESTINATION_TIME_CODE]
  }

  get destinationTime() {
    return this.destinationTimeCode ? this.facets.homewards.find(h => h.code === this.destinationTimeCode) : undefined
  }

  get departurePlaceArrivalTimeCode() {
    return this.raw[_PARAMS.DEPARTURE_PLACE_ARRIVAL_TIME_CODE]
  }

  get departurePlaceArrivalTime() {
    return this.departurePlaceArrivalTimeCode ? this.facets.returns.find(r => r.code === this.departurePlaceArrivalTimeCode) : undefined
  }

  get order() {
    return this.raw[_PARAMS.ORDER]
  }

  get code() {
    return this.raw[_PARAMS.CODE]
  }
}

// areas を渡すと名前解決機能が使えるようになる
export class Destination {
  constructor(regionId, countryId, cityId, districtId, hotelIds, additionals = {
    areas: new Areas(),
  }) {
    this.regionId = regionId
    this.countryId = countryId
    this.cityId = cityId
    this.districtId = districtId
    this.hotelIds = hotelIds || []
    this.areas = additionals.areas
  }

  get isSpecified() {
    return !!this.regionId || !!this.countryId || !! this.cityId || !!this.districtId
  }

  // 以下 areas が必要

  get regionName() {
    return this.areas.resolveRegionName(this.regionId)
  }

  get regionNameEn() {
    return this.areas.resolveRegionNameEn(this.regionId)
  }

  get countryName() {
    return this.areas.resolveCountryName(this.countryId)
  }

  get countryNameEn() {
    return this.areas.resolveCountryNameEn(this.countryId)
  }

  get cityName() {
    return this.areas.resolveCityName(this.cityId)
  }

  get cityNameEn() {
    return this.areas.resolveCityNameEn(this.cityId)
  }

  get districtName() {
    return this.areas.resolveDistrictName(this.districtId)
  }

  get districtNameEn() {
    return this.areas.resolveDistrictNameEn(this.districtId)
  }

  get name() {
    return this.districtName || this.cityName || this.countryName || this.regionName
  }

  get nameEn() {
    return this.districtNameEn || this.cityNameEn || this.countryNameEn || this.regionNameEn
  }
}

const filtersOf = pickSymbols(Object.values(_FILTER_PARAMS))
